import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Network, Layout, Link as LinkIcon, Users, Shield, TrendingUp, Zap, GitBranch, Globe } from 'lucide-react';
import './Platform.css';

const Platform = () => {
  const platformCapabilities = [
    {
      icon: <Network size={40} />,
      title: 'Multi-Agent Orchestration',
      description: 'Coordinate multiple AI agents to work together on complex workflows. Create agent teams that handle multi-step processes seamlessly.',
      benefits: ['Chain multiple agents', 'Parallel execution', 'Conditional logic', 'Error handling']
    },
    {
      icon: <Layout size={40} />,
      title: 'No-Code Studio',
      description: 'Build and deploy AI agents without writing code. Visual workflow builder with drag-and-drop interface for business users.',
      benefits: ['Visual workflow designer', 'Pre-built templates', 'Test & debug tools', 'One-click deployment']
    },
    {
      icon: <LinkIcon size={40} />,
      title: '1,500+ Integrations',
      description: 'Connect with your existing systems and tools seamlessly. Native integrations with popular business applications.',
      benefits: ['CRM & ERP systems', 'Communication tools', 'Cloud storage', 'Custom APIs']
    },
    {
      icon: <Users size={40} />,
      title: 'Human-in-the-Loop',
      description: 'Maintain control with human oversight and approval workflows. Agents escalate complex decisions to humans.',
      benefits: ['Approval workflows', 'Review queues', 'Feedback loops', 'Override controls']
    },
    {
      icon: <Shield size={40} />,
      title: 'Enterprise Security',
      description: 'SOC 2 certified, GDPR compliant infrastructure with comprehensive audit logs and encryption.',
      benefits: ['End-to-end encryption', 'Audit logs', 'Role-based access', 'Compliance certifications']
    },
    {
      icon: <TrendingUp size={40} />,
      title: 'Continuous Learning',
      description: 'Agents improve over time through feedback, monitoring, and automated retraining cycles.',
      benefits: ['Performance monitoring', 'A/B testing', 'Automated retraining', 'Version control']
    },
    {
      icon: <Zap size={40} />,
      title: 'Real-Time Processing',
      description: 'Process thousands of tasks simultaneously with sub-second response times and auto-scaling.',
      benefits: ['Sub-second latency', 'Auto-scaling', 'Load balancing', '99.9% uptime']
    },
    {
      icon: <GitBranch size={40} />,
      title: 'Version Control & Rollback',
      description: 'Track changes, test updates, and rollback to previous versions instantly if needed.',
      benefits: ['Git-like versioning', 'Staging environments', 'Instant rollback', 'Change history']
    },
    {
      icon: <Globe size={40} />,
      title: 'Multi-Language Support',
      description: 'Deploy agents that understand and respond in 100+ languages with automatic translation.',
      benefits: ['100+ languages', 'Auto-translation', 'Cultural awareness', 'Localization']
    }
  ];

  return (
    <div className="platform-page">
      <section className="platform-overview-section">
        <h2 className="display-large" style={{ textAlign: 'center' }}>Everything You Need to Build AI Automation</h2>
        <p className="body-medium" style={{ textAlign: 'center', marginTop: '24px', maxWidth: '900px', margin: '24px auto 0' }}>
          The platform provides the infrastructure, tools, and capabilities to deploy production-ready AI agents without the complexity of building from scratch.
        </p>

        <div className="capabilities-grid">
          {platformCapabilities.map((capability, idx) => (
            <div key={idx} className="capability-card">
              <div style={{ color: 'var(--brand-primary)' }}>{capability.icon}</div>
              <h3 className="heading-2" style={{ marginTop: '20px' }}>{capability.title}</h3>
              <p className="body-medium" style={{ marginTop: '16px' }}>{capability.description}</p>
              <ul className="benefits-list">
                {capability.benefits.map((benefit, bIdx) => (
                  <li key={bIdx} className="body-small">
                    <span style={{ color: 'var(--brand-primary)' }}>✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="how-platform-works">
        <h2 className="display-large" style={{ textAlign: 'center' }}>How Our Platform Works</h2>
        <div className="workflow-steps">
          <div className="workflow-step">
            <div className="step-number">01</div>
            <h3 className="heading-2">Choose or Build Your Agent</h3>
            <p className="body-medium" style={{ marginTop: '12px' }}>
              Select from 60+ pre-built agents or create custom agents using our no-code studio. Define workflows, triggers, and actions.
            </p>
          </div>
          <div className="workflow-step">
            <div className="step-number">02</div>
            <h3 className="heading-2">Connect Your Systems</h3>
            <p className="body-medium" style={{ marginTop: '12px' }}>
              Integrate with your existing tools and data sources. The platform handles authentication, data mapping, and API calls automatically.
            </p>
          </div>
          <div className="workflow-step">
            <div className="step-number">03</div>
            <h3 className="heading-2">Test & Deploy</h3>
            <p className="body-medium" style={{ marginTop: '12px' }}>
              Test your agent in a staging environment, then deploy to production with one click. Monitor performance in real-time.
            </p>
          </div>
          <div className="workflow-step">
            <div className="step-number">04</div>
            <h3 className="heading-2">Scale & Optimize</h3>
            <p className="body-medium" style={{ marginTop: '12px' }}>
              The platform automatically scales to handle increasing workloads. Continuous monitoring and learning improve performance over time.
            </p>
          </div>
        </div>
      </section>

      <section className="technical-specs">
        <h2 className="display-large" style={{ textAlign: 'center' }}>Built for Enterprise Scale</h2>
        <div className="specs-grid">
          <div className="spec-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>99.9%</h3>
            <p className="body-large" style={{ marginTop: '12px' }}>Uptime SLA</p>
            <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-muted)' }}>Enterprise-grade reliability</p>
          </div>
          <div className="spec-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>&lt;100ms</h3>
            <p className="body-large" style={{ marginTop: '12px' }}>Response Time</p>
            <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-muted)' }}>Sub-second performance</p>
          </div>
          <div className="spec-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>1,500+</h3>
            <p className="body-large" style={{ marginTop: '12px' }}>Integrations</p>
            <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-muted)' }}>Connect any system</p>
          </div>
          <div className="spec-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>∞</h3>
            <p className="body-large" style={{ marginTop: '12px' }}>Scalability</p>
            <p className="body-small" style={{ marginTop: '8px', color: 'var(--text-muted)' }}>Auto-scaling infrastructure</p>
          </div>
        </div>
      </section>

      <section className="platform-cta">
        <h2 className="display-large">Ready to Build Your AI Workforce?</h2>
        <p className="body-large" style={{ marginTop: '24px' }}>
          Start deploying AI agents today with our platform.
        </p>
        <div style={{ marginTop: '40px' }}>
          <Link to="/demo" className="btn-primary">
            Request Platform Access <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Platform;
